<div [formGroup]="form">
  <div class="page-header">
    <h1>Authorization and Consent</h1>
  </div>
  <div class="attention">
    <p>The authorization and consents requested below can only be provided by a person completing their own application, a parent completing the application for their minor child, a legal representative or a legal guardian. If you are any other person completing the application on behalf of another, please enter “N/A” in the required fields below.</p>
  </div>
  <p>Section 6 of the <i>Crime Victim Assistance Act</i> allows the Crime Victim Assistance Program to collect your personal information from persons and organizations for the purpose of determining your eligibility for benefits and provides the authority for these organizations to disclose your personal information to Crime Victim Assistance Program for the same purpose.</p>
  <p>By signing below, I am authorizing the Crime Victim Assistance Program to request a report or information from a medical practitioner, a health professional, an expert and/or one or more of the third parties listed below. I am also consenting to the Crime Victim Assistance Program contacting these third parties to seek verification of the medical, financial, or other employment information I have provided. I understand that the Crime Victim Assistance Program may contact and seek or verify information from these parties for the purpose of determining my eligibility for benefits:</p>

  <div [class.with-error]="!form.get('signName').valid && form.get('signName').touched">
    <p>I, <input class="form-control standard" type="text" formControlName="signName" placeholder="Please type your full name">, hereby authorize: <span class="error-states ng-star-inserted">*</span></p>
    @if (!form.get('signName').valid && form.get('signName').touched) {
      <div class="error-states error-container" style="margin-left: 16px">
        Please enter your full name
      </div>
    }
    <ol class="list punctuated">
      <li><span>The doctor, dentist, optometrist, chiropractor, or other health care professional who treated my injuries (physical and/or psychological), to obtain medical or other reports regarding my injuries, treatment or information relevant to this application.</span></li>
      <li><span>The police or other law enforcement authorities, to obtain copies of police reports, statements, incident reports or other information relevant to this application;</span></li>
      <li><span>The Workers' Compensation Board of BC or other authority from which I received or will receive or will be eligible to receive payments from provincial, federal or other jurisdictions funds, to obtain information relevant to this application;</span></li>
      <li><span>My employer(s) or similar authority, to obtain information as to my employment, earnings, benefits or other information relevant to this application;</span></li>
      <li><span>Any accident, disability, sickness, life insurance/assurance company or private pension scheme or extended health benefits scheme from which payments or services were received or will be received, to obtain information relevant to this application;</span></li>
      <li><span>Employment and Social Development Canada, Indigenous Services Canada or any other authority from which payments were received or will be received, to obtain information relevant to this application;</span></li>
      <li><span>Canada Revenue Agency or other similar agency in any jurisdiction to obtain information as to my employment income.</span></li>
      <li><span>The Ministry of Children and Family Development to obtain information relevant to this application.</span></li>
    </ol>
  </div>

  <section>
    <h2 class="blue-header">Standard Authorization & Signature</h2>
    <app-field [required]="true" [valid]="isFieldValid('approvedAuthorityNotification')" errorMessage="Please select whether you understand that the Crime Victim Assistance Program may notify the above authorities that you have submitted an application for benefits pursuant to the Crime Victim Assistance Act">
      <label>
        <input type="checkbox" [value]="true" name="approvedAuthorityNotification" formControlName="approvedAuthorityNotification">
        I understand that the Crime Victim Assistance Program may notify the above authorities that I have submitted an application for benefits pursuant to the <em>Crime Victim Assistance Act</em>. <span class="error-states">*</span>
      </label>
    </app-field>

    <app-field [required]="true" [valid]="isFieldValid('readAndUnderstoodTermsAndConditions')" errorMessage="Please select whether you have read and understood the above information">
      <label>
        <input type="checkbox" [value]="true" name="readAndUnderstoodTermsAndConditions" formControlName="readAndUnderstoodTermsAndConditions">
        I have read and understood the above information <span class="error-states">*</span>
      </label>
    </app-field>

    <ng-template #victimIsAMinor>If the victim is a minor (12-18 years) <strong>and</strong> seeking medical benefits, the victim's name and signature is required on this page.</ng-template>
    <app-field label="Signature" [required]="true" [valid]="isFieldValid('signature')" errorMessage="Please provide your digital signature" [tooltipReference]="formType === ApplicationType.Victim_Application ? victimIsAMinor : ''">
      @if (!hasSignature('signature')) {
        <div class="signature-trigger" (click)="showSignPad('signature')">
          <p>Click this box to sign</p>
        </div>
      }
      @if (hasSignature('signature')) {
        <div class="signature-preview">
          <img [src]="valueOrEmpty('signature')" alt="Digital signature" />
          <a class="redo" (click)="showSignPad('signature')">Redo Signature</a>
        </div>
      }
    </app-field>

    <h2 class="blue-header">Release of Information - Optional</h2>
    <app-field label="Crime Victim Assistance Program staff requires your written permission to discuss the information in your file with other persons. Please complete this section if you want to allow program staff to discuss your file with a victim services agency or another person, such as a family member." [valid]="isFieldValid('allowCvapStaffSharing')" errorMessage="Please select an option">
      <label class="inline-label">
        <input type="radio" [value]="CRMBoolean.True" name="allowCvapStaffSharing" formControlName="allowCvapStaffSharing">
        <span>Yes</span>
      </label>
      <label class="inline-label">
        <input type="radio" [value]="CRMBoolean.False" name="allowCvapStaffSharing" formControlName="allowCvapStaffSharing">
        <span>No</span>
      </label>
    </app-field>

    @if (form.get('allowCvapStaffSharing').value === CRMBoolean.True) {
      <section>
        @for (item of form.get('authorizedPerson')['controls']; track item; let i = $index) {
          <div class="authorizedPerson" formArrayName="authorizedPerson">
            <div [formGroupName]="i" class="authorizedPers">
              @if (showRemoveAuthorization) {
                <a href="javascript:void(0);" (click)="removeAuthorizationInformation(i)" class="float-right"><i class="fas fa-trash-alt"></i> Delete Authorization</a>
              }
              <h3>Authorized Person {{ i + 1 }}</h3>
              <div class="row">
                <div class="col-md-6">
                  <app-field label="Is this a person or an agency?" [required]="true" [valid]="isMyControlValid(item.get('providerType'))" errorMessage="Please select one">
                    <select class="form-control half-width" formControlName="providerType" (change)="setAuthPersonAgencyAndNameValidators(item)">
                      <option value="">Select...</option>
                      <option value="Person">Person</option>
                      <option value="Agency">Agency</option>
                    </select>
                  </app-field>
                </div>
              </div>
              <div class="row">
                @if (item.get('providerType').value === 'Agency') {
                  <div class="col-md-6">
                    <app-field label="Agency Name" [required]="item.get('providerType').value === 'Agency'" [valid]="isMyControlValid(item.get('authorizedPersonAgencyName'))" errorMessage="Please enter an agency">
                      <input class="form-control" type="text" formControlName="authorizedPersonAgencyName">
                    </app-field>
                  </div>
                }
                <div class="col-md-6">
                  <app-field label="Email Address" [valid]="isMyControlValid(item.get('authorizedPersonEmail'))" errorMessage="Please enter an email address">
                    <input class="form-control" type="text" (blur)="trimEmail(item.get('authorizedPersonEmail'))" formControlName="authorizedPersonEmail" maxlength="100">
                  </app-field>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6">
                  <app-field label="First Name" [required]="item.get('providerType').value === 'Person'" [valid]="isMyControlValid(item.get('authorizedPersonFirstName'))" errorMessage="Please enter first name">
                    <input class="form-control" type="text" formControlName="authorizedPersonFirstName">
                  </app-field>
                </div>
                <div class="col-md-6">
                  <app-field label="Last Name" [required]="item.get('providerType').value === 'Person'" [valid]="isMyControlValid(item.get('authorizedPersonLastName'))" errorMessage="Please enter last name">
                    <input class="form-control" type="text" formControlName="authorizedPersonLastName">
                  </app-field>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6">
                  <app-field label="Relationship to Applicant" [required]="item.get('providerType').value === 'Person'" errorMessage="Please select a relationship" [valid]="isMyControlValid(item.get('authorizedPersonRelationship'))">
                    <select class="form-control" formControlName="authorizedPersonRelationship">
                      <option value="">Select...</option>
                      @for (item of relationshipList; track item) {
                        <option [value]="item">{{ item }}</option>
                      }
                    </select>
                  </app-field>
                  <div>
                    @if (item.get('authorizedPersonRelationship').value == 'Other') {
                      <input class="form-control mt-2 mb-3" type="text" formControlName="authorizedPersonRelationshipOther">
                    }
                  </div>
                </div>
                <div class="col-md-6">
                  <app-field label="Phone Number (incl. area code)" [valid]="isMyControlValid(item.get('authorizedPersonPhoneNumber'))" errorMessage="Please enter a valid phone number">
                    @if (item.get('authorizedPersonAgencyAddress.country').value === 'Canada' || item.get('authorizedPersonAgencyAddress.country').value === 'United States of America') {
                      <input class="form-control" mask='(000) 000 0000' type="text" formControlName="authorizedPersonPhoneNumber">
                    } @else {
                      <input class="form-control" type="text" formControlName="authorizedPersonPhoneNumber" digitOnly>
                    }
                  </app-field>
                </div>
              </div>
              <app-address (change)="setAuthPersonPhoneValidators(item)" [group]="item.get('authorizedPersonAgencyAddress')" [showChildrenAsRequired]="false"></app-address>
            </div>
          </div>
        }
        @if (showAddAuthorizationInformation) {
          <a href="javascript:void(0);" class="btn btn-secondary add-auth-btn" (click)="addAuthorizationInformation(false)">Add an Authorization</a>
        }
        <app-field [valid]="isFieldValid('authorizedPersonAuthorizesDiscussion')" errorMessage="Please check this box to indicate that you accept Declaration" [required]="true">
          <label>
            <input type="checkbox" [value]="true" name="authorizedPersonAuthorizesDiscussion" formControlName="authorizedPersonAuthorizesDiscussion" [required]="true">
            I hereby give my consent to the Crime Victim Assistance Program staff to discuss my claim (e.g. status and benefit eligibility) with the above person(s) or agency for the purpose of assisting me in the application process.
            <br>
              The Crime Victim Assistance Program may provide or share my financial, medical, and employment information with this authorized contact. <span class="error-states ng-star-inserted">*</span>
            </label>
          </app-field>
          <app-field label="Signature" [required]="true" [valid]="isFieldValid('authorizedPersonSignature')" errorMessage="Please provide your digital signature">
            @if (!hasSignature('authorizedPersonSignature')) {
              <div class="signature-trigger" (click)="showSignPad('authorizedPersonSignature')">
                <p>Click this box to sign</p>
              </div>
            }
            @if (hasSignature('authorizedPersonSignature')) {
              <div class="signature-preview">
                <img [src]="valueOrEmpty('authorizedPersonSignature')" alt="Digital signature" />
                <a class="redo" (click)="showSignPad('authorizedPersonSignature')">Redo Signature</a>
              </div>
            }
          </app-field>
        </section>
      }
    </section>
  </div>

